import React from "react";
import { DigitalShareAppType } from "../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../Enums/DigitalShareItemType";
import { DigitalShareSectionEnum } from "../Enums/DigitalShareSectionEnum";
import {
    createDigitalShareItem,
    IDigitalShareItem,
} from "../Models/IDigitalShareItem";
import GPSInsightsDigitalShareTemplate from "../Components/Apps/GPSInsights/DigitalShare/Templates/GPSInsightsDigitalShareTemplate";

import RetailMatchDigitalShareTemplate from "../Components/Apps/RetailMatch/DigitalShare/Templates/RetailMatchDigitalShareTemplate";
import html2canvas from "html2canvas";
import { RenderType } from "../Enums/RenderType";
import { featureFlags } from "../Common/CurrentFeatureFlags";

export const HH_ATTR_STATIC_TEXT
    = "Based on actual household attributes (not census data) which is updated every four (4) weeks.";
export const GPSInsightsTemplateDisclaimer
    = "Disclaimer: lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. ";
const DIGISHARE_CDN = `/Images/${encodeURIComponent("Digital Share")}`;

export const digitalShareRender = (
    app: DigitalShareAppType,
    itemType: DigitalShareItemType,
    children: React.ReactNode
): React.ReactElement => {
    const digitalShareOutputs: IDigitalShareItem[] = getDigitalShareOutputs(app);
    const item: IDigitalShareItem = digitalShareOutputs.find(
        (o) => o.type === itemType
    );
    if (item == undefined) {
        return;
    }

    if (app === DigitalShareAppType.Mobilytics) {
        return (
            <GPSInsightsDigitalShareTemplate
                polygonComparisons={[]}
                captionAboveTitle={item.captionAboveTitle}
                sectionTitle={item.sectionTitle}
                disclaimer={GPSInsightsTemplateDisclaimer}
                itemType={itemType}
                hideItemTypeTitle={item.hideItemTitle}
                hideItemText={item.hideItemText}
            >
                {children}
            </GPSInsightsDigitalShareTemplate>
        );
    } else if (app === DigitalShareAppType.RetailMatch) {
        return (
            <RetailMatchDigitalShareTemplate
                captionAboveTitle={item.captionAboveTitle}
                sectionTitle={item.sectionTitle}
                disclaimer={GPSInsightsTemplateDisclaimer}
                itemType={item.itemTitle}
                hideItemTypeTitle={item.hideItemTitle}
                hideItemText={item.hideItemText}
            >
                {children}
            </RetailMatchDigitalShareTemplate>
        );
    }
};

export const getDigitalShareMapImage = async (
    mapElement: HTMLElement,
    width = 860,
    height = 504
): Promise<JSX.Element> => {
    const canvas = await html2canvas(mapElement, {
        // This ensures that the library is able to fetch images from a different domain, like
        // the google map tiles. Without it, the google map won't load.
        useCORS: true,

        // This is the device pixel ratio. If not explicitly set to 1, it will use whatever
        // the current device/monitor's pixel ratio is, which will likely be 1.5 or 2 for high-
        // density pixel displays like Apple Retina displays, or high-resolution monitors
        // set to display apps and text at 150% (or anything non-100%) in Windows display settings.
        scale: 1,

        // Need full size here, otherwise it will be blurry.
        // Alternately, we could provide exact size as used on img tag below, but
        //   it won't contain the entire contents of what was showing on the page.
        // width: parseInt(mapElement.style.width),
        // height: parseInt(mapElement.style.height),

        // Leaving logging enabled causes major lag.
        logging: false,
    });

    const dataUrl = canvas.toDataURL();

    // Resize here since it will use the browser's native image scaling methods.
    return <img src={dataUrl} width={width} height={height} />;
};

export const digitalShareMapRender = async (
    app: DigitalShareAppType,
    itemType: DigitalShareItemType,
    mapElement: HTMLElement,
    ref: React.MutableRefObject<HTMLDivElement>
): Promise<React.ReactElement> => {
    if (app === DigitalShareAppType.Mobilytics) {
        const digitalShareOutputs = getDigitalShareOutputs(app);
        const item = digitalShareOutputs.find((o) => o.type === itemType);

        const img = await getDigitalShareMapImage(mapElement);

        return (
            <div ref={ref}>
                <GPSInsightsDigitalShareTemplate
                    polygonComparisons={[]}
                    captionAboveTitle={item.captionAboveTitle}
                    sectionTitle={item.itemTitle}
                    disclaimer={GPSInsightsTemplateDisclaimer}
                    itemType={item.itemTitle}
                >
                    {img}
                </GPSInsightsDigitalShareTemplate>
            </div>
        );
    }
};

export const getIsDigitalShare = (renderType: RenderType): boolean => {
    return featureFlags.DigitalShare && renderType === RenderType.DIGITAL_SHARE;
};

export const filterGpsOutputsAgainstAvailableReports = (
    outputs: IDigitalShareItem[],
    hasVisitorLoyaltyData: boolean,
    hasFvpData: boolean,
    hasTradeArea: boolean
): IDigitalShareItem[] => {
    if (!hasVisitorLoyaltyData) {
        outputs = outputs.filter(
            (output) => output.type !== DigitalShareItemType.VisitorLoyalty
        );
    }

    if (!hasFvpData) {
        outputs = outputs.filter(
            (output) => output.type !== DigitalShareItemType.FrequentlyVisitedPlaces
        );
    }

    if (!hasTradeArea) {
        outputs = outputs.filter(
            (output) => output.type !== DigitalShareItemType.VisitorTradeArea
        );
    }

    return outputs;
};

/* 
  GPS Sections
  Trade Area
    - visitor map
    - visitor trade area
    - visitor origination
  Household Characteristics
    - psych profile
    - psych summary
    - key profile attributes
    - marriage
    - children
    - number of persons
    - head of household age
    - race
    - household income  
    - education 
    - dwelling
    - home ownership 
    - new home owner 
    - new mover 
  Volume
    - relative volume metric
    - visit volume trend by week
    - visit volume by day of week
    - visit volume by day
    - volume by hour of day
  Visitor Behaviors
    - FVP
    - visitor loyalty
    - visitor activity 
*/

export const retailMatchDigitalShareOutputs = () => {
    const sectionNumberMap = new Map();

    sectionNumberMap.set(DigitalShareSectionEnum.BRAND_COMPARISON, "01");
    sectionNumberMap.set(DigitalShareSectionEnum.KEY_METRICS, "02");
    sectionNumberMap.set(DigitalShareSectionEnum.LOCATIONS_ANALYZED, "03");
    sectionNumberMap.set(DigitalShareSectionEnum.PSYCHOGRAPHIC_PROFILE, "04");
    sectionNumberMap.set(DigitalShareSectionEnum.HH_COMPOSITION, "005");

    const outputs = [
        {
            type: DigitalShareItemType.BrandComparison,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/Brand_Comparison.PNG`,
            sectionTitle: DigitalShareSectionEnum.BRAND_COMPARISON,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.BrandComparison,
            hideItemTitle: true,
            hideItemText: true,
        },
        {
            type: DigitalShareItemType.KeyMetrics,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/KeyMetrics.png`,
            sectionTitle: DigitalShareSectionEnum.KEY_METRICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.KeyMetrics,
            hideItemTitle: true,
            hideItemText: true,
        },
        {
            type: DigitalShareItemType.LocationsAnalyzed,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/LocationsAnalyzed.png`,
            sectionTitle: DigitalShareSectionEnum.KEY_METRICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.LocationsAnalyzed,
            hideItemTitle: true,
            hideItemText: true,
        },
        {
            type: DigitalShareItemType.PsychographicProfile,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Psych-Profile.jpg`,
            sectionTitle: DigitalShareSectionEnum.KEY_METRICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.PsychographicProfile,
            hideItemTitle: true,
            hideItemText: true,
        },
        {
            type: DigitalShareItemType.PsychographicSummary,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Psych-Summary.jpg`,
            sectionTitle: DigitalShareSectionEnum.KEY_METRICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.PsychographicSummary,
            hideItemTitle: true,
            hideItemText: true,
        },
        {
            type: DigitalShareItemType.Marriage,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Marriage.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_COMPOSITION,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.Marriage,
        },
        {
            type: DigitalShareItemType.Children,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Children.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_COMPOSITION,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.Children,
        },
        {
            type: DigitalShareItemType.NumberOfPersons,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Number-of-Persons.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_COMPOSITION,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.NumberOfPersons,
        },
        {
            type: DigitalShareItemType.HeadOfHouseholdAge,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-HH-Age.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_COMPOSITION,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.HeadOfHouseholdAge,
        },
        {
            type: DigitalShareItemType.RaceAndEthnicity,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Race.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_COMPOSITION,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.RaceAndEthnicity,
        },
        {
            type: DigitalShareItemType.HouseholdIncome,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Income.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_COMPOSITION,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.HouseholdIncome,
        },
        {
            type: DigitalShareItemType.HouseholdEducationLevel,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Education.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_COMPOSITION,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.HouseholdEducationLevel,
        },
        {
            type: DigitalShareItemType.DwellingType,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Dwelling.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_COMPOSITION,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.DwellingType,
        },
        {
            type: DigitalShareItemType.HomeOwnership,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Home-Ownership.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_COMPOSITION,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.HomeOwnership,
        },
    ];

    outputs.forEach(
        (o) => (o.captionAboveTitle = sectionNumberMap.get(o.sectionTitle))
    );

    return outputs;
};

export const gpsDigitalShareOutputs = (
    isBenchmark: boolean
): IDigitalShareItem[] => {
    const sectionNumberMap = new Map();

    sectionNumberMap.set(DigitalShareSectionEnum.TRADE_AREA, "01");
    sectionNumberMap.set(DigitalShareSectionEnum.HH_CHARACTERISTICS, "02");
    sectionNumberMap.set(DigitalShareSectionEnum.VOLUME, "03");
    sectionNumberMap.set(DigitalShareSectionEnum.VISITOR_BEHAVIORS, "04");

    const outputs: IDigitalShareItem[] = [
        {
            type: DigitalShareItemType.VisitorMap,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport--Visitor-Map.jpg`,
            sectionTitle: DigitalShareSectionEnum.TRADE_AREA,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.VisitorMap,
        },
        {
            type: DigitalShareItemType.VisitorTradeArea,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Trade-Area-01.jpg`,
            sectionTitle: DigitalShareSectionEnum.TRADE_AREA,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.VisitorTradeArea,
        },
        // {
        //     type: DigitalShareItemType.VisitorOrigination,
        //     isSelected: false,
        //     imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Origination-01.jpg`,
        //     sectionTitle: DigitalShareSectionEnum.TRADE_AREA,
        //     captionAboveTitle: "",
        //     itemTitle: DigitalShareItemType.VisitorOrigination,
        // },
        {
            type: DigitalShareItemType.PsychographicProfile,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Psych-Profile.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.PsychographicProfile,
            hideItemTitle: true,
            hideItemText: true,
        },
        {
            type: DigitalShareItemType.PsychographicSummary,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Psych-Summary.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.PsychographicSummary,
            hideItemTitle: true,
            hideItemText: true,
        },
        // {
        //     type: DigitalShareItemType.KeyProfileAttributes,
        //     isSelected: false,
        //     imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Key-Profile.jpg`,
        //     sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
        //     captionAboveTitle: "",
        //     itemTitle: DigitalShareItemType.KeyProfileAttributes,
        // },
        {
            type: DigitalShareItemType.Marriage,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Marriage.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.Marriage,
        },
        {
            type: DigitalShareItemType.Children,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Children.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.Children,
        },
        {
            type: DigitalShareItemType.NumberOfPersons,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Number-of-Persons.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.NumberOfPersons,
        },
        {
            type: DigitalShareItemType.HeadOfHouseholdAge,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-HH-Age.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.HeadOfHouseholdAge,
        },
        {
            type: DigitalShareItemType.RaceAndEthnicity,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Race.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.RaceAndEthnicity,
        },
        {
            type: DigitalShareItemType.HouseholdIncome,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Income.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.HouseholdIncome,
        },
        {
            type: DigitalShareItemType.HouseholdEducationLevel,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Education.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.HouseholdEducationLevel,
        },
        {
            type: DigitalShareItemType.DwellingType,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Dwelling.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.DwellingType,
        },
        {
            type: DigitalShareItemType.HomeOwnership,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Home-Ownership.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.HomeOwnership,
        },
        {
            type: DigitalShareItemType.NewHomeOwner,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-New-Homeowner.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.NewHomeOwner,
        },
        {
            type: DigitalShareItemType.NewMover,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-New-Mover.jpg`,
            sectionTitle: DigitalShareSectionEnum.HH_CHARACTERISTICS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.NewMover,
        },
        {
            type: DigitalShareItemType.RelativeVolume,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Volume-Metric.jpg`,
            sectionTitle: DigitalShareSectionEnum.VOLUME,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.RelativeVolume,
            hideItem: isBenchmark,
        },
        {
            type: DigitalShareItemType.VolumeByWeek,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Volume-Trend.jpg`,
            sectionTitle: DigitalShareSectionEnum.VOLUME,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.VolumeByWeek,
        },
        {
            type: DigitalShareItemType.VolumeByDayOfWeek,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Day-of-Week.jpg`,
            sectionTitle: DigitalShareSectionEnum.VOLUME,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.VolumeByDayOfWeek,
        },
        // {
        //     type: DigitalShareItemType.VolumeByDay,
        //     isSelected: false,
        //     imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-By-Day.jpg`,
        //     sectionTitle: DigitalShareSectionEnum.VOLUME,
        //     captionAboveTitle: "",
        //     itemTitle: DigitalShareItemType.VolumeByDay,
        // },
        {
            type: DigitalShareItemType.VolumeByHourOfDay,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Hour-of-Day.jpg`,
            sectionTitle: DigitalShareSectionEnum.VOLUME,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.VolumeByHourOfDay,
        },
        {
            type: DigitalShareItemType.VolumeByMetrics,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/Volume-Metrics-Table.png`,
            sectionTitle: DigitalShareSectionEnum.VOLUME,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.VolumeByMetrics,
        },
        {
            type: DigitalShareItemType.FrequentlyVisitedPlaces,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Freq-Visited-Places.jpg`,
            sectionTitle: DigitalShareSectionEnum.VISITOR_BEHAVIORS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.FrequentlyVisitedPlaces,
        },
        {
            type: DigitalShareItemType.FrequentlyVisitedPlacesMap,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Freq-Visited-Places.jpg`,
            sectionTitle: DigitalShareSectionEnum.VISITOR_BEHAVIORS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.FrequentlyVisitedPlacesMap,
        },
        {
            type: DigitalShareItemType.VisitorLoyalty,
            isSelected: false,
            imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Loyalty.jpg`,
            sectionTitle: DigitalShareSectionEnum.VISITOR_BEHAVIORS,
            captionAboveTitle: "",
            itemTitle: DigitalShareItemType.VisitorLoyalty,
        },
        // {
        //     type: DigitalShareItemType.VisitorActivity,
        //     isSelected: false,
        //     imgUrl: `${DIGISHARE_CDN}/MobilyticsExport-Activity.jpg`,
        //     sectionTitle: DigitalShareSectionEnum.VISITOR_BEHAVIORS,
        //     captionAboveTitle: "",
        //     itemTitle: DigitalShareItemType.VisitorActivity,
        // },
    ];

    outputs.forEach(
        (o) => (o.captionAboveTitle = sectionNumberMap.get(o.sectionTitle))
    );

    return outputs.filter((o) => !o.hideItem);
};

export const getDigitalShareOutputs = (app: string): IDigitalShareItem[] => {
    let outputs = [];

    switch (app) {
        case DigitalShareAppType.Mobilytics:
            outputs = gpsDigitalShareOutputs(false);
            break;

        case DigitalShareAppType.RetailMatch:
            outputs = retailMatchDigitalShareOutputs();
            break;

        default:
            break;
    }

    return Array.from(
        outputs,
        ({
            type,
            isSelected,
            imgUrl,
            sectionTitle,
            itemTitle,
            captionAboveTitle,
            hideItemTitle,
            hideItemText,
        }) =>
            createDigitalShareItem(
                type,
                isSelected,
                imgUrl,
                sectionTitle,
                captionAboveTitle,
                itemTitle,
                hideItemTitle,
                hideItemText
            )
    );
};

export const getPrintOutputs = (
    app: DigitalShareAppType,
    outputs: IDigitalShareItem[]
): IDigitalShareItem[] => {
    /* BEGIN TEMPORARY EXCLUSIONS FOR PRINT 
        Print automatically renders everything, so we have to explicitly exclude components
        that aren't supported yet
    */
    const exclusions = {
        Mobilytics: [
            // DigitalShareItemType.FrequentlyVisitedPlaces,
            // DigitalShareItemType.RelativeVolume,
            // DigitalShareItemType.KeyProfileAttributes,
            // DigitalShareItemType.PsychographicProfile,
            // DigitalShareItemType.PsychographicSummary,
        ],
        "Retail Match": [
            // DigitalShareItemType.LocationsAnalyzed,
            // DigitalShareItemType.PsychographicProfile,
            // DigitalShareItemType.PsychographicSummary,
        ],
    };

    //Please leave this here for testing
    // const exclusionsBrad = {
    //     "Mobilytics": [
    //         DigitalShareItemType.VisitorTradeArea,
    //         DigitalShareItemType.VisitorOrigination,
    //         DigitalShareItemType.KeyProfileAttributes,
    //         DigitalShareItemType.NewHomeOwner,
    //         DigitalShareItemType.NewMover,
    //         DigitalShareItemType.RelativeVolume,
    //         DigitalShareItemType.VolumeByWeek,
    //         DigitalShareItemType.VolumeByDayOfWeek,
    //         DigitalShareItemType.VolumeByDay,
    //         DigitalShareItemType.FrequentlyVisitedPlaces,
    //         DigitalShareItemType.VisitorLoyalty,
    //         DigitalShareItemType.VisitorActivity,
    //         DigitalShareItemType.VolumeByHourOfDay,
    //         DigitalShareItemType.Children,
    //         DigitalShareItemType.Marriage,
    //         DigitalShareItemType.NumberOfPersons,
    //         DigitalShareItemType.HeadOfHouseholdAge,
    //         DigitalShareItemType.RaceAndEthnicity,
    //         DigitalShareItemType.HouseholdIncome,
    //         DigitalShareItemType.HouseholdEducationLevel,
    //         DigitalShareItemType.DwellingType,
    //         DigitalShareItemType.HomeOwnership,
    //         DigitalShareItemType.PsychographicProfile,
    //         DigitalShareItemType.PsychographicSummary,
    //     ],
    //     "Retail Match": [
    //         DigitalShareItemType.LocationsAnalyzed,
    //         DigitalShareItemType.PsychographicProfile,
    //         DigitalShareItemType.PsychographicSummary
    //     ]
    // };
    outputs = outputs.filter((o) => exclusions[app].indexOf(o.type) === -1);
    /* END TEMPORARY EXCLUSIONS FOR PRINT */

    return Array.from(
        outputs,
        ({
            type,
            isSelected,
            imgUrl,
            sectionTitle,
            itemTitle,
            captionAboveTitle,
        }) =>
            createDigitalShareItem(
                type,
                isSelected,
                imgUrl,
                sectionTitle,
                captionAboveTitle,
                itemTitle
            )
    );
};

export const delay = async (delayMs: number) => {
    return new Promise<void>((resolve: () => void, reject: () => void) => {
        setTimeout(() => {
            resolve();
        }, delayMs);
    });
};
